/**
 * TaggrContext.tsx - React context for Taggr
 * 
 * CHANGE HISTORY:
 * - 251102_0627 [pending] danielbeer: Initial creation
 */

import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { TaggrManager } from './TaggrManager';
import type { Tag, TagGroup, TaggrOptions } from './types';

interface TaggrContextValue {
  taggr: TaggrManager | null;
  tags: Tag[];
  groups: TagGroup[];
  isLoaded: boolean;
  refresh: () => Promise<void>;
}

const TaggrContext = createContext<TaggrContextValue | null>(null);

export interface TaggrProviderProps {
  children: React.ReactNode;
  taggr?: TaggrManager;
  options?: Partial<TaggrOptions>;
}

// Create a context for Organizr (will be provided by OrganizrProvider)
const OrganizrContext = createContext<any>(null);

export const TaggrProvider: React.FC<TaggrProviderProps> = ({
  children,
  taggr: providedTaggr,
  options
}) => {
  // Get Organizr from OrganizrProvider context
  const organizrContext = useContext(OrganizrContext);
  const organizr = organizrContext?.organizr || (window as any).__organizr__;
  
  const [taggr] = useState(() => {
    if (providedTaggr) return providedTaggr;
    if (!organizr) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('[Taggr] No Organizr available, TaggrManager will not be initialized');
      }
      return null;
    }
    return new TaggrManager({ ...options, organizr } as TaggrOptions);
  });
  const [tags, setTags] = useState<Tag[]>([]);
  const [groups, setGroups] = useState<TagGroup[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  const refresh = async () => {
    if (!taggr) {
      setIsLoaded(true);
      return;
    }
    setTags(taggr.getAllTags());
    setGroups(taggr.getAllGroups());
    setIsLoaded(true);
  };

  useEffect(() => {
    refresh();
  }, [taggr]);

  // Memoize context value to prevent infinite re-renders
  const value = useMemo<TaggrContextValue>(() => ({
    taggr,
    tags,
    groups,
    isLoaded,
    refresh
  }), [taggr, tags, groups, isLoaded, refresh]);

  return (
    <TaggrContext.Provider value={value}>
      {children}
    </TaggrContext.Provider>
  );
};

export const useTaggr = (): TaggrContextValue => {
  const context = useContext(TaggrContext);
  if (!context) {
    throw new Error('useTaggr must be used within a TaggrProvider');
  }
  if (!context.taggr) {
    console.warn('[Taggr] TaggrManager not initialized - Organizr dependency missing');
  }
  return context;
};

// Convenience hooks
export const useTags = (): Tag[] => {
  const { tags } = useTaggr();
  return tags;
};

export const useGroups = (): TagGroup[] => {
  const { groups } = useTaggr();
  return groups;
};

export const useTagOperations = () => {
  const { taggr, refresh } = useTaggr();

  return {
    createTag: async (name: string, options?: any) => {
      if (!taggr) throw new Error('TaggrManager not initialized');
      const tag = await taggr.createTag(name, options);
      await refresh();
      return tag;
    },
    updateTag: async (nameOrId: string, updates: Partial<Tag>) => {
      if (!taggr) throw new Error('TaggrManager not initialized');
      const tag = await taggr.updateTag(nameOrId, updates);
      await refresh();
      return tag;
    },
    deleteTag: async (nameOrId: string) => {
      if (!taggr) throw new Error('TaggrManager not initialized');
      const result = await taggr.deleteTag(nameOrId);
      await refresh();
      return result;
    },
    tagItem: async (itemId: string, tags: string | string[]) => {
      if (!taggr) throw new Error('TaggrManager not initialized');
      await taggr.tagItem(itemId, tags);
      await refresh();
    },
    untagItem: async (itemId: string, tag: string) => {
      if (!taggr) throw new Error('TaggrManager not initialized');
      await taggr.untagItem(itemId, tag);
      await refresh();
    },
    getItemTags: (itemId: string) => {
      if (!taggr) return [];
      return taggr.getItemTags(itemId);
    },
    searchByTags: (tags: string[], options?: any) => {
      if (!taggr) return [];
      return taggr.searchByTags(tags, options);
    }
  };
};
